import ButtonPanel from "app/game/ButtonPanel";
import useGameState, {
  GameAction,
  useGameActionDispatch,
} from "app/game/useGameState";
import {
  GODOT_EVENT,
  GODOT_MESSAGE,
  sendGodotMessage,
  useGodotListener,
  type BoardInitializedEvent,
  type BoardStateEvent,
} from "app/godot-embed/godot-bridge";
import ReactGodot from "app/godot-embed/ReactGodot";
import Loader from "app/ui/Loader";
import cn from "app/util/classnames";
import useDebugKeypress from "app/util/useDebugKeys";
import useImageUrlToBase64 from "app/util/useImageUrlToBase64";
import useWindowSize, { Aspect } from "app/util/useWindowSize";
import { useCallback, useState, useEffect } from "react";

// If the board layout is bigger than this in either dimension, it will be
// counted as a 'big' board, and the player will be allowed to zoom/rotate.
const BIG_BOARD_SIZE = 5;

interface GameProps {
  levelLayout: string; // Stringified JSON array
  victoryImageUrl: string; // The image displayed at the end of the game complete animation

  // Whether the board state should be saved/loaded in local storage
  saveBoardState: boolean;

  isAlreadyComplete: boolean;
  onBackButtonClick: () => void;
}

function Game(props: GameProps) {
  const [hideGame, setHideGame] = useState(true);
  const [isLoaded, setIsLoaded] = useState(false);
  const [prevLayout, setPrevLayout] = useState(props.levelLayout);
  const [isGameplayActive, setIsGameplayActive] = useState(false);
  // The aspect ratio of the game board, so we can shape the canvas accordingly
  const [boardAspect, setBoardAspect] = useState(0);
  const gameDispatch = useGameActionDispatch();
  const { boardState, isAudioMuted } = useGameState();
  const { aspect: screenAspect } = useWindowSize();
  const isLandscape = screenAspect === Aspect.LANDSCAPE;

  // Force reload when the level layout changes
  if (props.levelLayout !== prevLayout) {
    setIsLoaded(false);
    setPrevLayout(props.levelLayout);
  }

  const handleGameLoaded = useCallback(() => {
    setHideGame(true);

    // Make sure the game initializes with the saved audio mute state
    sendGodotMessage(
      GODOT_MESSAGE.SET_AUDIO_MUTE,
      isAudioMuted ? "true" : "false",
    );

    const savedState = props.saveBoardState ? boardState : "[]";
    sendGodotMessage(
      GODOT_MESSAGE.INIT_BOARD,
      props.levelLayout,
      savedState,
      screenAspect === Aspect.PORTRAIT ? "true" : "false",
      props.isAlreadyComplete ? "true" : "false",
    );
    setIsLoaded(true);
  }, [
    props.saveBoardState,
    props.levelLayout,
    props.isAlreadyComplete,
    boardState,
    screenAspect,
    isAudioMuted,
  ]);

  function handleGameUnloaded() {
    setIsLoaded(false);
    setHideGame(true);
    setIsGameplayActive(false);
  }

  // Send the vicotry image to GOdot as a base 64 after gameplay starts
  const base64VictoryImg = useImageUrlToBase64(props.victoryImageUrl);
  useEffect(() => {
    if (!isGameplayActive || !base64VictoryImg) {
      return;
    }
    sendGodotMessage(GODOT_MESSAGE.SET_VICTORY_IMAGE, base64VictoryImg);
  }, [isGameplayActive, base64VictoryImg]);

  function handleGodotBoardInitialized(eventData: BoardInitializedEvent) {
    setBoardAspect(eventData.aspectRatio);
    setHideGame(false);
  }
  useGodotListener(GODOT_EVENT.BOARD_INITIALIZED, handleGodotBoardInitialized);

  // This enables the button panel right when input is enabled in-game
  useGodotListener(GODOT_EVENT.GAMEPLAY_START, () => setIsGameplayActive(true));

  // This hides the button panel when the final cell is filled in-game
  useGodotListener(GODOT_EVENT.SOLVED, () => {
    setIsGameplayActive(false);
  });

  const isScreenPhonePortrait =
    window.innerHeight > window.innerWidth && window.innerWidth < 768;

  const handleGodotBoardStateChanged = useCallback(
    (eventData: BoardStateEvent) => {
      if (!props.saveBoardState) {
        return;
      }

      // Save new board state in local storage
      const { boardState } = eventData;
      gameDispatch({
        type: GameAction.BOARD_STATE_UDPATED,
        payload: { newBoardState: boardState },
      });
    },
    [props.saveBoardState, gameDispatch],
  );

  useGodotListener(
    GODOT_EVENT.BOARD_STATE_CHANGED,
    handleGodotBoardStateChanged,
  );

  function handleCanvasDimensionsChanged(
    canvasW: number,
    canvasH: number,
    divW: number,
    divH: number,
  ) {
    const newW = Math.min(canvasW, divW);
    const newH = Math.min(canvasH, divH);

    sendGodotMessage(
      GODOT_MESSAGE.SET_DIMENSIONS,
      newW.toString(),
      newH.toString(),
      window.innerHeight > window.innerWidth ? "true" : "false", // Whether page is portrait
    );
  }

  // Allow skipping of game by holding three keys at once
  useDebugKeypress(["w", "i", "n"], 1000, () => {
    sendGodotMessage(GODOT_MESSAGE.DEBUG_TRIGGER_VICTORY);
  });

  // Made a function for this because the button panel will be instantiated
  // at a different place in the heirarchy depending on the aspect ratio.
  const createButtonPanel = useCallback(() => {
    return (
      <ButtonPanel
        isLandscape={isLandscape}
        hide={!isGameplayActive}
        showBackButton={props.isAlreadyComplete}
        onBackButtonClick={props.onBackButtonClick}
        allowZoomAndRotate={
          isScreenPhonePortrait &&
          !isLayoutPortrait(props.levelLayout) &&
          isLayoutBig(props.levelLayout)
        }
      />
    );
  }, [
    isLandscape,
    isGameplayActive,
    props.isAlreadyComplete,
    isScreenPhonePortrait,
    props.levelLayout,
    props.onBackButtonClick,
  ]);

  return (
    <div // Full screen container
      className={cn(
        "w-full h-dvh flex flex-col overflow-hidden",
        "bg-gameplay-bg",
      )}
    >
      <div // Gameplay components (changes layout with screen aspect)
        className={cn(
          "w-full h-full min-w-0 min-h-0 flex overflow-hidden",
          isLandscape ? "flex-row" : "flex-col",
        )}
      >
        <ReactGodot
          key={props.levelLayout}
          script={"pixellence.js"}
          executable="pixellence"
          resize={true}
          onGameLoaded={handleGameLoaded}
          onGameUnloaded={handleGameUnloaded}
          hideCanvas={hideGame}
          aspectRatio={boardAspect}
          onDimensionsChanged={handleCanvasDimensionsChanged}
          wrapperClassName={cn(
            isLandscape ? "flex-row flex-[2_1_auto]" : "flex-col flex-1",
            "min-h-0 min-w-0 overflow-hidden",
          )}
        >
          {isLandscape && (
            <div // Button panel holder for landscape mode
              className="flex-none px-2"
            >
              {createButtonPanel()}
            </div>
          )}
        </ReactGodot>
        {!isLandscape && (
          <div // Button panel holder for portrait mode
            className={"flex-none flex flex-col justify-end pb-2"}
          >
            {createButtonPanel()}
          </div>
        )}
      </div>
      {!isLoaded && (
        <div
          className={cn(
            "absolute inset-0",
            "flex justify-center items-center",
            "bg-gameplay-bg",
          )}
        >
          <Loader className="grow max-w-50 max-h-50" />
        </div>
      )}
    </div>
  );
}

export default Game;

function getLayoutDimensions(boardLayout: string): {
  width: number;
  height: number;
} {
  const layout = JSON.parse(boardLayout) as Array<Array<number>>;
  return { width: layout[0].length, height: layout.length };
}

function isLayoutPortrait(boardLayout: string): boolean {
  const { width, height } = getLayoutDimensions(boardLayout);
  return height > width;
}

function isLayoutBig(boardLayout: string): boolean {
  const { width, height } = getLayoutDimensions(boardLayout);
  return width > BIG_BOARD_SIZE || height > BIG_BOARD_SIZE;
}